import { QRCodeSVG } from 'qrcode.react'
import { type FC, type MouseEvent, memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import CloseIcon from 'assets/svg/app/close.svg'
import InfoIcon from 'assets/svg/app/info-white.svg'
import SnxLogo from 'assets/svg/partners/snx.svg'
import CopyCheckIcon from 'assets/svg/referrals/copy-check.svg'
import Button from 'components/Button'
import UnderlineTabs from 'components/Button/UnderlineTabs'
import DropdownField from 'components/DropdownField'
import Input from 'components/Input/Input'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import {
	FlexDivCol,
	FlexDivColCentered,
	FlexDivRow,
	FlexDivRowCentered,
} from 'components/layout/flex'
import { GridDiv } from 'components/layout/grid'
import { useCopyToClipboard } from 'hooks/useCopyToClipboard'
import DepositInfo from 'sections/accounts/Deposits/DepositInfo'
import {
	DepositTab,
	MIN_DEPOSIT_AMOUNT,
	chainOptions,
	coinOptions,
} from 'sections/accounts/constants'
import { SectionHeading } from 'sections/shared/components/SectionHeading'
import { setOpenModal } from 'state/app/reducer'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectSignerWallet } from 'state/wallet/selectors'
import { resetButtonCSS } from 'styles/common'

type DepositPanelProps = {
	onDismiss: () => void
}

const DepositPanel: FC<DepositPanelProps> = memo(({ onDismiss }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const signerWallet = useAppSelector(selectSignerWallet)
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const { copyToClipboard, CopyTooltip } = useCopyToClipboard()
	const coinsList = useMemo(
		() => coinOptions.filter(({ providers }) => providers.includes(perpsProvider)),
		[perpsProvider]
	)
	const chainsList = useMemo(
		() => chainOptions.filter(({ providers }) => providers.includes(perpsProvider)),
		[perpsProvider]
	)
	const [selectedCoin, setSelectedCoin] = useState(coinsList[0].value)
	const [selectedChain, setSelectedChain] = useState(chainsList[0].value)

	const depositTabs = useMemo(
		() => [
			{
				name: DepositTab.CRYPTO,
				label: t('account.deposit-section.tabs.crypto-deposit'),
				active: true,
				onClick: () => {},
			},
			{
				name: DepositTab.FIAT,
				label: t('account.deposit-section.tabs.fiat'),
				active: false,
				disabled: true,
				onClick: () => {},
			},
		],
		[t]
	)

	const STEPS = useMemo(
		() =>
			[1, 2, 3].map((step) => ({
				step,
				active: step === 1,
				marginTop: -88 + step * 86,
			})),
		[]
	)

	return (
		<Container>
			<ContentContainer rowGap="15px">
				<FlexDivRowCentered>
					<SectionHeading title={t('account.deposit-section.title')} />
					<DismissButton onClick={onDismiss}>
						<CloseIcon width={12} height={12} />
					</DismissButton>
				</FlexDivRowCentered>
				<TabButtonsContainer>
					<UnderlineTabs options={depositTabs} noBorders={true} />
				</TabButtonsContainer>
				<GridContainer>
					<FlexDivCol>
						<StepLine />
						{STEPS.map(({ step, active, marginTop }) => (
							<Step key={step} $active={active} $marginTop={marginTop}>
								{step}
							</Step>
						))}
					</FlexDivCol>
					<FlexDivCol rowGap="15px">
						<DropdownField
							label={t('account.deposit-section.choose-coin')}
							options={coinsList}
							value={selectedCoin}
							onChange={setSelectedCoin}
						/>
						<DropdownField
							label={t('account.deposit-section.choose-chain')}
							options={chainsList}
							value={selectedChain}
							onChange={setSelectedChain}
						/>
						<FlexDivCol rowGap="10px">
							<FlexDivRowCentered>
								<FlexDivRowCentered columnGap="5px">
									<Body color="primary">{t('account.deposit-section.deposit-account')}</Body>
									<InfoIcon width={12} height={12} />
								</FlexDivRowCentered>
								<FlexDivRowCentered columnGap="5px">
									<SnxLogo />
									<Body weight="bold">{t('account.deposit-section.kwenta')}</Body>
								</FlexDivRowCentered>
							</FlexDivRowCentered>
							<FlexDivRowCentered columnGap="5px" justifyContent="flex-start">
								<Body color="secondary">{t('account.deposit-section.only-evm')}</Body>
								<InfoIcon width={12} height={12} />
							</FlexDivRowCentered>
						</FlexDivCol>
					</FlexDivCol>
				</GridContainer>
				<FlexDivColCentered rowGap="10px">
					{signerWallet && (
						<QRCodeSVGContainer size={120} value={signerWallet} level="Q" includeMargin={true} />
					)}
					<AddressInputContainer rowGap="8px">
						<Body color="secondary">{t('account.deposit-section.evm-address')}</Body>
						<AddressInput value={signerWallet} readOnly />
						<CopyCheckIconContainer justifyContent="flex-start" columnGap="5px">
							<Body color="primary">{t('account.deposit-section.copy-button')}</Body>
							<StyledCopyCheckIcon
								width={12}
								height={12}
								onClick={(event: MouseEvent) => copyToClipboard(event, signerWallet)}
							/>
						</CopyCheckIconContainer>
					</AddressInputContainer>
				</FlexDivColCentered>
				<FlexDivRowCentered>
					<FlexDivRowCentered justifyContent="flex-start" columnGap="5px">
						<Body color="secondary">{t('account.deposit-section.minimum-deposit-amount')}</Body>
						<InfoIcon width={12} height={12} />
					</FlexDivRowCentered>
					<Body color="primary" weight="semiBold" mono>
						{MIN_DEPOSIT_AMOUNT} {selectedCoin}
					</Body>
				</FlexDivRowCentered>
				<Divider height={1} divider />
				<DepositInfo />
				<Divider height={1} divider />
				<FlexDivCol rowGap="8px">
					<Body color="secondary">{t('account.deposit-section.important')}</Body>
					<InfoContainer columnGap="4px">
						<InfoIconContainer>
							<InfoIcon width={14} height={14} />
						</InfoIconContainer>
						<Body color="secondary" style={{ lineHeight: '18px' }}>
							{t('account.deposit-section.important-info')}
						</Body>
					</InfoContainer>
				</FlexDivCol>
				<Body color="secondary" style={{ lineHeight: '18px' }}>
					{t('account.deposit-section.copy')}
				</Body>
			</ContentContainer>
			<Footer>
				<Button
					size="small"
					variant="solid-highlight"
					fullWidth
					onClick={() => dispatch(setOpenModal('manage_wallet'))}
				>
					{t('account.deposit-section.done-button')}
				</Button>
			</Footer>
			<CopyTooltip />
		</Container>
	)
})

const Container = styled(FlexDivCol)`
	position: relative;
	height: 100%;
	min-height: 400px;
	overflow-y: scroll;
`

const QRCodeSVGContainer = styled(QRCodeSVG)`
	border-radius: 4px;
`

const GridContainer = styled(GridDiv)`
	grid-template-columns: 10% 90%;
`

const InfoIconContainer = styled.div`
	width: 5%;
	margin-top: 2px;
`

const ContentContainer = styled(FlexDivCol)`
	padding: 20px;
	margin-bottom: 60px;
`

const Footer = styled.div`
	position: fixed;
	bottom: 0;
	width: 100%;
	border-top: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
	padding: 15px;
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.cards.background};
`

const InfoContainer = styled(FlexDivRow)`
	background-color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.pill.yellowGray.background};
	border-radius: 8px;
	padding: 10px;
`

const DismissButton = styled.button`
	${resetButtonCSS};
	position: absolute;
	top: 25px;
	right: 20px;
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	&:hover {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`

const AddressInputContainer = styled(FlexDivCol)`
	position: relative;
`

const CopyCheckIconContainer = styled(FlexDivRowCentered)`
	position: absolute;
	margin-top: 33px;
	right: 2%;
`

const StyledCopyCheckIcon = styled(CopyCheckIcon)`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.secondary};
`

const AddressInput = styled(Input)`
	width: 360px;
	height: 35px;
	border-radius: 8px;
	font-size: 11px;
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.secondary};
`

const Step = styled(FlexDivColCentered)<{ $active: boolean; $marginTop: number }>`
	position: absolute;
	margin-top: ${(props) => props.$marginTop}px;
	justify-content: center;
	width: 20px;
	height: 20px;
	border-radius: 50%;
	font-weight: bold;
	color: white;
	background-color: ${(props) =>
		props.$active
			? props.theme.colors.selectedTheme.newTheme.button.solid.background
			: props.theme.colors.selectedTheme.newTheme.pill.yellowGray.border};
`

const StepLine = styled.div`
	position: absolute;
	margin-top: 0px;
	margin-left: 10px;
	height: 190px;
	border: 0.5px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
`

const TabButtonsContainer = styled.div`
	display: flex;
	min-height: 46px;
	gap: 15px;

	button {
		font-size: 13px;
	}

	> div > div {
		padding-left: 0px;
	}

	border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
`

const Divider = styled(Spacer)`
	width: 100%;
`

export default DepositPanel
